import type { SupabaseClient } from '@supabase/supabase-js'
import {
  COLUMNAS_ASIGNACION_SIN_PRECIO,
  preciosDeAsignaciones,
  serviciosService,
  type ClienteServicioPrecio,
} from './serviciosService'
import { leCorrespondeEstePeriodo, getMesFromPeriodo } from '@/lib/billing-utils'
import { fetchInChunks } from '@/lib/postgrest-chunks'
import type { Database } from '@/types/database'

type MovimientoInsert = Database['public']['Tables']['movimientos']['Insert']

export interface ResumenHonorarioCliente {
  cliente_id: string
  cliente_nombre: string
  cliente_cuit: string
  honorarios_servicios: number
  honorarios_total: number
  servicios: (ClienteServicioPrecio & { servicio_nombre: string })[]
}

export interface ResultadoGeneracionLote {
  creados: number
  omitidos: number
  total: number
  montoTotal: number
  detalle: {
    cliente_id: string
    cliente_nombre: string
    estado: 'creado' | 'omitido'
    monto?: number
    razon?: string
    /** Movimientos que le corresponden a este cliente: uno por servicio sin movimiento y con precio mayor a cero. */
    movimientos?: number
    /** Cargo no mensual del mismo mes con el mismo monto que un servicio del lote. */
    posibleDuplicado?: { monto: number; servicio: string }
  }[]
}

/**
 * Otro lote insertó deudas mensuales del mismo período entre la lectura y el insert, y el insert
 * chocó con idx_movimientos_deuda_mensual_unica (23505). No se duplicó nada.
 */
export class LoteConcurrenteError extends Error {
  constructor() {
    super('Otro usuario generó honorarios de este período mientras tanto. Volvé a abrir la vista previa para ver lo que falta.')
    this.name = 'LoteConcurrenteError'
  }
}

type ServicioConNombre = ClienteServicioPrecio & { servicio_nombre: string }

/** Campos de un servicio del cliente que el lote usa para armar su deuda. */
export type ServicioDeLote = Pick<
  ClienteServicioPrecio,
  'servicio_id' | 'precio' | 'activo' | 'frecuencia' | 'mes_inicio'
> & { servicio_nombre: string }

/** Deuda mensual que el lote le genera a un cliente por un servicio. */
export interface DeudaDeLote {
  servicio_id: string
  concepto: string
  monto: number
  tipo_deuda: 'mensual'
  mes_anio: string
}

/**
 * Filtra los servicios activos y no únicos que corresponden facturar en el mes del período.
 * Un servicio sin frecuencia cuenta como mensual y uno sin mes de inicio arranca en enero.
 */
export const serviciosDelPeriodo = <T extends ServicioDeLote>(servicios: T[], mesAnio: string): T[] => {
  const mesPeriodo = getMesFromPeriodo(mesAnio)
  return servicios.filter(s =>
    s.activo &&
    s.frecuencia !== 'unico' &&
    leCorrespondeEstePeriodo(s.frecuencia ?? 'mensual', s.mes_inicio ?? 1, mesPeriodo)
  )
}

/**
 * Arma la deuda del lote de un cliente para un período, una fila por servicio del período con
 * precio mayor que cero, en el orden de los servicios.
 * El concepto sale de la plantilla, que reemplaza {periodo}, {cliente} y {servicio}.
 */
export const armarDeudasDeLote = ({
  servicios,
  mesAnio,
  conceptoTemplate,
  nombreCliente,
}: {
  servicios: ServicioDeLote[]
  mesAnio: string
  conceptoTemplate: string
  nombreCliente: string
}): DeudaDeLote[] =>
  serviciosDelPeriodo(servicios, mesAnio)
    .filter(s => s.precio > 0)
    .map(s => ({
      servicio_id: s.servicio_id,
      concepto: conceptoTemplate
        .replaceAll('{periodo}', mesAnio)
        .replaceAll('{cliente}', nombreCliente)
        .replaceAll('{servicio}', s.servicio_nombre),
      monto: s.precio,
      tipo_deuda: 'mensual',
      mes_anio: mesAnio,
    }))

/**
 * Servicio para gestión de honorarios.
 *
 * IMPORTANTE: Todos los métodos reciben el cliente Supabase autenticado como primer parámetro.
 * Usar con useSupabase() desde el hook que llame a estos métodos.
 */
export const honorariosService = {
  /**
   * Servicios activos con su precio, agrupados por cliente_id.
   * El precio sale de vista_precios_servicios_cliente. Sin verHonorariosCliente llega en 0.
   * Con exigirPrecio, un precio que la vista no entrega lanza un error en vez de llegar en 0.
   */
  async _obtenerServiciosBatch(
    client: SupabaseClient<Database>,
    clienteIds?: string[],
    { exigirPrecio = false }: { exigirPrecio?: boolean } = {}
  ): Promise<Map<string, ServicioConNombre[]>> {
    type Fila = Omit<ClienteServicioPrecio, 'precio'> & { servicios: { nombre: string } | null }
    const leer = async (lote?: string[]): Promise<Fila[]> => {
      let query = client
        .from('cliente_servicio_precios')
        .select(`${COLUMNAS_ASIGNACION_SIN_PRECIO}, servicios(nombre)`)
        .eq('activo', true)
      if (lote) query = query.in('cliente_id', lote)

      const { data, error } = await query
      if (error) throw error
      return (data ?? []) as unknown as Fila[]
    }

    const filas = clienteIds && clienteIds.length > 0 ? await fetchInChunks(clienteIds, leer) : await leer()
    const precios = await preciosDeAsignaciones(client, filas.map(item => item.id))
    const map = new Map<string, ServicioConNombre[]>()
    for (const item of filas) {
      const clienteId = item.cliente_id
      const list = map.get(clienteId) ?? []
      const precio = precios.get(item.id)
      if (exigirPrecio && precio == null) {
        throw new Error(
          'los honorarios no se pueden calcular porque falta el permiso para ver el precio de un servicio del cliente'
        )
      }
      list.push({
        ...item,
        precio: precio ?? 0,
        servicio_nombre: item.servicios?.nombre ?? '',
      })
      map.set(clienteId, list)
    }
    return map
  },

  // Obtener resumen de honorarios de un cliente (single)
  async obtenerResumenCliente(client: SupabaseClient<Database>, clienteV2Id: string): Promise<ResumenHonorarioCliente> {
    const { data: cliente, error: errorCliente } = await client
      .from('clientes')
      .select(`
        id,
        personas(nombre_completo, cuit)
      `)
      .eq('id', clienteV2Id)
      .single()

    if (errorCliente) throw errorCliente

    const clienteData = cliente as {
      id: string
      personas: { nombre_completo: string; cuit: string }
    }

    const servicios = await serviciosService.obtenerServiciosCliente(client, clienteV2Id)
    const honorariosServicios = servicios.reduce((total, s) => total + s.precio, 0)

    return {
      cliente_id: clienteV2Id,
      cliente_nombre: clienteData.personas?.nombre_completo ?? '',
      cliente_cuit: clienteData.personas?.cuit ?? '',
      honorarios_servicios: honorariosServicios,
      honorarios_total: honorariosServicios,
      servicios,
    }
  },

  // Obtener resumen de todos los clientes activos — BATCH (2 queries total)
  async obtenerResumenTodos(client: SupabaseClient<Database>): Promise<ResumenHonorarioCliente[]> {
    // Query 1: All active clients with persona data
    const { data: clientes, error } = await client
      .from('clientes')
      .select('id, personas(nombre_completo, cuit)')
      .eq('estado', 'activo')

    if (error) throw error

    const clienteIds = (clientes ?? []).map(c => c.id)
    if (clienteIds.length === 0) return []

    // Query 2: ALL active service prices in one batch
    const serviciosPorCliente = await this._obtenerServiciosBatch(client, clienteIds)

    // Build resumenes in memory (0 extra queries)
    const resumenes: ResumenHonorarioCliente[] = []
    for (const cliente of clientes ?? []) {
      const servicios = serviciosPorCliente.get(cliente.id) ?? []
      if (servicios.length === 0) continue

      const honorariosServicios = servicios.reduce((total, s) => total + s.precio, 0)
      if (honorariosServicios <= 0) continue

      const clienteData = cliente as {
        id: string
        personas: { nombre_completo: string; cuit: string } | null
      }

      resumenes.push({
        cliente_id: cliente.id,
        cliente_nombre: clienteData.personas?.nombre_completo ?? '',
        cliente_cuit: clienteData.personas?.cuit ?? '',
        honorarios_servicios: honorariosServicios,
        honorarios_total: honorariosServicios,
        servicios,
      })
    }

    return resumenes.sort((a, b) => a.cliente_nombre.localeCompare(b.cliente_nombre))
  },

  // Generar movimientos en lote — BATCH (3 queries + 1 insert)
  async generarMovimientosEnLote(
    client: SupabaseClient<Database>,
    mesAnio: string,
    conceptoTemplate: string,
    fecha: string,
    organizacionId: string,
    clienteIds?: string[]
  ): Promise<ResultadoGeneracionLote> {
    // Query 1: Active clients with persona data
    const { data: clientes, error: errorClientes } = await client
      .from('clientes')
      .select(`
        id,
        estado,
        personas(nombre_completo, cuit)
      `)
      .eq('estado', 'activo')

    if (errorClientes) throw errorClientes

    let clientesFiltrados = clientes ?? []
    if (clienteIds && clienteIds.length > 0) {
      clientesFiltrados = clientesFiltrados.filter(c => clienteIds.includes(c.id))
    }

    const ids = clientesFiltrados.map(c => c.id)
    if (ids.length === 0) {
      return { creados: 0, omitidos: 0, total: 0, montoTotal: 0, detalle: [] }
    }

    // Query 2: Existing movimientos for this period
    const { data: movExistentes, error: errorExistentes } = await client
      .from('movimientos')
      .select('cliente_id, servicio_id')
      .eq('mes_anio', mesAnio)
      .eq('tipo', 'Deuda')
      .eq('tipo_deuda', 'mensual')
    // Sin las deudas del período, el lote generaría lo que ya existe.
    if (errorExistentes) throw errorExistentes

    const movExistentesSet = new Set(
      movExistentes?.map(m => `${m.cliente_id}|${m.servicio_id ?? 'null'}`) ?? []
    )

    // Query 3: ALL service prices for these clients in one batch
    const serviciosPorCliente = await this._obtenerServiciosBatch(client, ids, { exigirPrecio: true })

    // Process in memory (0 extra queries)
    const movimientos: MovimientoInsert[] = []
    const detalle: ResultadoGeneracionLote['detalle'] = []

    for (const cliente of clientesFiltrados) {
      const clienteData = cliente as {
        id: string
        personas: { nombre_completo: string; cuit: string } | null
      }
      const nombreCliente = clienteData.personas?.nombre_completo ?? 'Sin nombre'

      // Filter servicios for this period
      const servicios = serviciosDelPeriodo(serviciosPorCliente.get(cliente.id) ?? [], mesAnio)

      if (servicios.length > 0) {
        const deudas = armarDeudasDeLote({
          servicios: servicios.filter(s => !movExistentesSet.has(`${cliente.id}|${s.servicio_id}`)),
          mesAnio,
          conceptoTemplate,
          nombreCliente,
        })

        for (const deuda of deudas) {
          movimientos.push({
            cliente_id: cliente.id,
            servicio_id: deuda.servicio_id,
            concepto: deuda.concepto,
            fecha,
            debe: deuda.monto,
            haber: 0,
            monto: deuda.monto,
            tipo: 'Deuda',
            tipo_deuda: deuda.tipo_deuda,
            mes_anio: deuda.mes_anio,
            cuenta_id: null,
            created_by: 'sistema',
            organizacion_id: organizacionId,
          })
        }

        const montoTotalCliente = deudas.reduce((sum, d) => sum + d.monto, 0)
        const tienePendientes = servicios.some(s => !movExistentesSet.has(`${cliente.id}|${s.servicio_id}`))

        if (deudas.length > 0) {
          detalle.push({
            cliente_id: cliente.id,
            cliente_nombre: nombreCliente,
            estado: 'creado',
            monto: montoTotalCliente,
          })
        } else {
          detalle.push({
            cliente_id: cliente.id,
            cliente_nombre: nombreCliente,
            estado: 'omitido',
            // Mismos motivos que previsualizarGeneracion.
            razon: tienePendientes ? 'Sin honorarios' : 'Ya tiene movimientos para este período',
          })
        }
      } else {
        detalle.push({
          cliente_id: cliente.id,
          cliente_nombre: nombreCliente,
          estado: 'omitido',
          razon: 'Sin servicios que correspondan al período',
        })
      }
    }

    // Insert: 1 batch insert
    if (movimientos.length > 0) {
      const { error } = await client.from('movimientos').insert(movimientos)
      if (error?.code === '23505') throw new LoteConcurrenteError()
      if (error) throw error
    }

    const montoTotal = movimientos.reduce((sum, m) => sum + (m.debe ?? 0), 0)

    return {
      creados: movimientos.length,
      omitidos: detalle.filter(d => d.estado === 'omitido').length,
      total: clientesFiltrados.length,
      montoTotal,
      detalle,
    }
  },

  // Vista previa de generación — BATCH (4 queries total)
  async previsualizarGeneracion(
    client: SupabaseClient<Database>,
    mesAnio: string,
    clienteIds?: string[]
  ): Promise<ResultadoGeneracionLote['detalle']> {
    const mesPeriodo = getMesFromPeriodo(mesAnio)

    // Query 1: Active clients
    const { data: clientes, error: errorClientes } = await client
      .from('clientes')
      .select(`
        id,
        estado,
        personas(nombre_completo)
      `)
      .eq('estado', 'activo')

    if (errorClientes) throw errorClientes

    let clientesFiltrados = clientes ?? []
    if (clienteIds && clienteIds.length > 0) {
      clientesFiltrados = clientesFiltrados.filter(c => clienteIds.includes(c.id))
    }

    const ids = clientesFiltrados.map(c => c.id)
    if (ids.length === 0) return []

    // Query 2: Existing movimientos
    const { data: movExistentes, error: errorExistentes } = await client
      .from('movimientos')
      .select('cliente_id, servicio_id')
      .eq('mes_anio', mesAnio)
      .eq('tipo', 'Deuda')
      .eq('tipo_deuda', 'mensual')
    // Sin las deudas del período, el lote generaría lo que ya existe.
    if (errorExistentes) throw errorExistentes

    const movExistentesSet = new Set(
      movExistentes?.map(m => `${m.cliente_id}|${m.servicio_id ?? 'null'}`) ?? []
    )

    // Query 3: ALL service prices in one batch
    const serviciosPorCliente = await this._obtenerServiciosBatch(client, ids, { exigirPrecio: true })

    // Query 4: cargos no mensuales del mes (los manuales entran como esporadica, o sin tipo_deuda)
    const cargosManuales = await fetchInChunks(ids, async batch => {
      const { data, error } = await client
        .from('movimientos')
        .select('cliente_id, debe')
        .eq('mes_anio', mesAnio)
        .eq('tipo', 'Deuda')
        .or('tipo_deuda.is.null,tipo_deuda.neq.mensual')
        .in('cliente_id', batch)
      if (error) throw error
      return data ?? []
    })

    const centavos = (n: number | string | null) => Math.round(Number(n ?? 0) * 100)
    const montosManuales = new Map<string, Set<number>>()
    for (const c of cargosManuales) {
      if (!c.cliente_id) continue
      const montos = montosManuales.get(c.cliente_id) ?? new Set<number>()
      montos.add(centavos(c.debe))
      montosManuales.set(c.cliente_id, montos)
    }

    // Process in memory
    const detalle: ResultadoGeneracionLote['detalle'] = []

    for (const cliente of clientesFiltrados) {
      const clienteData = cliente as {
        id: string
        personas: { nombre_completo: string } | null
      }
      const nombreCliente = clienteData.personas?.nombre_completo ?? 'Sin nombre'

      const todosServicios = serviciosPorCliente.get(cliente.id) ?? []
      const serviciosQueCorresponden = todosServicios.filter(s =>
        s.activo &&
        s.frecuencia !== 'unico' &&
        leCorrespondeEstePeriodo(s.frecuencia ?? 'mensual', s.mes_inicio ?? 1, mesPeriodo)
      )

      if (serviciosQueCorresponden.length === 0) {
        detalle.push({
          cliente_id: cliente.id,
          cliente_nombre: nombreCliente,
          estado: 'omitido',
          razon: 'Sin servicios que correspondan al período',
        })
        continue
      }

      const serviciosSinMovimiento = serviciosQueCorresponden.filter(
        s => !movExistentesSet.has(`${cliente.id}|${s.servicio_id}`)
      )

      if (serviciosSinMovimiento.length === 0) {
        detalle.push({
          cliente_id: cliente.id,
          cliente_nombre: nombreCliente,
          estado: 'omitido',
          razon: 'Ya tiene movimientos para este período',
        })
        continue
      }

      // La generación crea un movimiento por servicio con precio mayor a cero. Los servicios sin
      // precio no se cuentan.
      const serviciosAGenerar = serviciosSinMovimiento.filter(s => s.precio > 0)
      const monto = serviciosAGenerar.reduce((sum, s) => sum + s.precio, 0)

      if (monto > 0) {
        const montosDelCliente = montosManuales.get(cliente.id)
        const servicioDuplicado = montosDelCliente
          ? serviciosAGenerar.find(s => montosDelCliente.has(centavos(s.precio)))
          : undefined
        detalle.push({
          cliente_id: cliente.id,
          cliente_nombre: nombreCliente,
          estado: 'creado',
          monto,
          movimientos: serviciosAGenerar.length,
          ...(servicioDuplicado && {
            posibleDuplicado: { monto: servicioDuplicado.precio, servicio: servicioDuplicado.servicio_nombre },
          }),
        })
      } else {
        detalle.push({
          cliente_id: cliente.id,
          cliente_nombre: nombreCliente,
          estado: 'omitido',
          razon: 'Sin honorarios',
        })
      }
    }

    return detalle.sort((a, b) => a.cliente_nombre.localeCompare(b.cliente_nombre))
  },
}
